import { X } from "@phosphor-icons/react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import * as React from "react"

import { cn } from "~/utils/helpers"

import { withPermissionGate } from "./PermissionTooltip"

const DialogRoot = DialogPrimitive.Root

const DialogTrigger = withPermissionGate(DialogPrimitive.Trigger, "DialogTrigger")

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "data-[state=open]:overlay-backdrop-enter data-[state=closed]:overlay-backdrop-exit fixed inset-0 z-50 bg-neutral-950/70 backdrop-blur-[1px]",
      className,
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { closeButton?: boolean }
>(({ className, children, closeButton = true, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "data-[state=open]:overlay-enter data-[state=closed]:overlay-exit origin-center focus-visible:outline-none fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border border-base bg-base px-6 py-5 shadow-modal rounded-[16px] md:w-full",
        className,
      )}
      aria-describedby={undefined}
      {...props}
    >
      {children}
      {closeButton && (
        <DialogPrimitive.Close className="focus:ring-ring data-[state=open]:text-fg-subtle absolute right-5 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent hover:cursor-pointer p-0.5">
          <X size="16" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogContentV2 = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { closeButton?: boolean }
>(({ className, children, closeButton = true, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "data-[state=open]:overlay-enter data-[state=closed]:overlay-exit origin-center focus-visible:outline-none fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] bg-base p-0 shadow-modal rounded-[16px] md:w-full",
        className,
      )}
      aria-describedby={undefined}
      {...props}
    >
      {children}
      {closeButton && (
        <DialogPrimitive.Close className="focus:ring-ring data-[state=open]:text-fg-subtle absolute right-5 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-hidden focus:ring-2 focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent hover:cursor-pointer p-0.5">
          <X size="16" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContentV2.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogHeaderV2 = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-col px-5 py-4 text-center w-full border-b border-base rounded-t-[16px] sm:text-left",
      className,
    )}
    {...props}
  />
)
DialogHeaderV2.displayName = "DialogHeaderV2"

const DialogBodyV2 = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col max-h-[70vh] overflow-y-auto px-5 py-4 gap-4", className)}
    {...props}
  >
    {children}
  </div>
))
DialogBodyV2.displayName = "DialogBodyV2"

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-row items-center", className)} {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogFooterV2 = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "flex flex-row items-center border-t border-base px-5 py-4 bg-subtle rounded-b-[16px]",
      className,
    )}
    {...props}
  />
)
DialogFooterV2.displayName = "DialogFooterV2"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title ref={ref} className={cn("label-base", className)} {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-fg-muted text-sm", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogRoot,
  DialogTitle,
  DialogTrigger,
  DialogContentV2,
  DialogHeaderV2,
  DialogBodyV2,
  DialogFooterV2,
}
